/**
 * Photographs waiting to be sent, and the rules for accepting one at all.
 *
 * WHY THIS IS NOT IN pendingQueue.ts. That queue is one JSON string in
 * localStorage, deliberately: a synchronous write cannot be half-finished by a
 * tab closing, and the whole thing either parses or does not. The price is the
 * ~5MB origin cap, shared with everything else on the origin. A phone photo as
 * base64 is 1-3MB, so TWO of them fill it — and the failure mode of a full
 * quota is a throw MID-WRITE, which would destroy the queue holding somebody
 * else's unsent inspection. Photos therefore live in IndexedDB, which is sized
 * for them, and the queue holds a reference the length of a UUID.
 *
 * THE THREE RULES, in the order they are applied to a capture:
 *
 *   1. DOWNSCALE FIRST. A modern phone camera produces 4-12MB at 4000px wide.
 *      Nothing about evidence of a leaking valve needs 4000px. The image is
 *      re-encoded to fit MAX_EDGE at JPEG quality 0.8 before its size is even
 *      measured, which turns most captures from "refused" into "fine".
 *
 *   2. A PER-PHOTO CAP. After downscaling, anything still over MAX_PHOTO_BYTES
 *      is refused — one enormous image must not be able to fill the device on
 *      its own and block every capture after it.
 *
 *   3. REFUSE AT THE POINT OF TAKING. Checked against the browser's own quota
 *      estimate BEFORE the photo is accepted, and again for real by attempting
 *      the write. Telling somebody their device is full is kinder than
 *      accepting their work and dropping it — which is the loss this whole
 *      mechanism exists to end, and the one a photo is most likely to cause.
 *
 * A blob is deleted only when the queue item that owns it has been confirmed
 * by the server, or refused outright by it. Never on a failed send.
 */

import { getSignedInUserId } from '@/utils/api';

const DB_NAME = 'pending-captures';
/** Between the owner's user id and the rest of a photo id (A16). */
export const OWNER_SEP = '/';
const DB_VERSION = 1;
const STORE = 'photos';

/** Longest edge after downscaling. Evidence, not photography. */
export const MAX_EDGE = 1600;

/** JPEG quality for the re-encode. 0.8 is the knee: below it, text on a
 *  label plate starts to go. */
export const JPEG_QUALITY = 0.8;

/** Refused above this, after downscaling. */
export const MAX_PHOTO_BYTES = 2 * 1024 * 1024;

/** How much room a photo must find free before it is accepted. Three times its
 *  own size: IndexedDB overheads are real, the estimate is approximate, and
 *  the next capture should not be the one that discovers the device is full. */
export const HEADROOM_FACTOR = 3;

/** The device cannot take this photo. The capture must be refused and said so
 *  — never accepted and dropped. */
export class DeviceFullError extends Error {
  constructor(message?: string) {
    super(
      message ||
      'This device has no room left to store the photo. Send what is already ' +
      'waiting, or free up space, and take it again — nothing was saved.',
    );
    this.name = 'DeviceFullError';
  }
}

/** The photo itself is the problem, not the device. */
export class PhotoTooLargeError extends Error {
  constructor(bytes: number) {
    super(
      `This photo is ${(bytes / 1024 / 1024).toFixed(1)}MB after being resized, ` +
      `over the ${MAX_PHOTO_BYTES / 1024 / 1024}MB limit for one photo. ` +
      'Take it again from further back, or with a lower camera resolution — ' +
      'nothing was saved.',
    );
    this.name = 'PhotoTooLargeError';
  }
}

// ---------------------------------------------------------------------------
// The store
// ---------------------------------------------------------------------------

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new DeviceFullError('This browser cannot store photos offline.'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB unavailable'));
  });
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then((db) => new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const request = run(t.objectStore(STORE));
    // Resolve on the TRANSACTION, not the request: a write request succeeds
    // before the transaction commits, and a quota failure surfaces on the
    // commit. Resolving early would report a photo as stored that is not.
    t.oncomplete = () => resolve(request.result);
    t.onerror = () => reject(t.error ?? request.error ?? new Error('IndexedDB write failed'));
    t.onabort = () => reject(t.error ?? new Error('IndexedDB write aborted'));
  }));
}

/** An id for a photo about to be stored.
 *
 * Minted by the CAPTURE SURFACE, before the queue item exists, because the
 * photo is accepted or refused the moment it is taken and the item that will
 * carry it may never be submitted at all. `sweepOrphanPhotos` in pendingQueue
 * cleans up the ones that are not.
 *
 * 🔴 KEYED BY PERSON (FM Technician A16). The id starts with the signed-in
 * user's id, so a photo belongs to one person's queue the way its item does:
 * the sweep that runs for whoever signs in next touches only their own (see
 * photoBelongsTo), and never deletes another person's unsent evidence. */
export function newPhotoId(): string {
  const c = globalThis.crypto;
  const rand = c && typeof c.randomUUID === 'function'
    ? c.randomUUID()
    : `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  const uid = getSignedInUserId();
  return uid ? `${uid}${OWNER_SEP}${rand}` : rand;
}

/** Is this stored photo the given person's? A photo id with no owner prefix
 *  was stored before photos were keyed by person; its item was adopted by
 *  whoever signed in next (pendingQueue adoptLegacy), so it counts as theirs. */
export function photoBelongsTo(id: string, uid: string): boolean {
  const at = id.indexOf(OWNER_SEP);
  return at < 0 || id.slice(0, at) === uid;
}

/** Store a photo and return the id the queue item should carry. */
export async function putPhoto(id: string, blob: Blob): Promise<void> {
  try {
    await tx('readwrite', (s) => s.put(blob, id));
  } catch {
    // QuotaExceededError, private-mode restrictions, a store the browser has
    // switched off. All of them mean one thing to the person holding the
    // phone: this photo is not saved, so do not tell them it is.
    throw new DeviceFullError();
  }
}

/** The stored photo, or null if it is gone — cleared site data, a wiped
 *  profile. Null is a real answer and the caller has to handle it. */
export async function getPhoto(id: string): Promise<Blob | null> {
  try {
    return (await tx<Blob | undefined>('readonly', (s) => s.get(id))) ?? null;
  } catch {
    return null;
  }
}

export async function deletePhoto(id: string): Promise<void> {
  try {
    await tx('readwrite', (s) => s.delete(id));
  } catch {
    /* an orphan blob is untidy; failing the send over it would be worse */
  }
}

export async function photoIds(): Promise<string[]> {
  try {
    const keys = await tx<IDBValidKey[]>('readonly', (s) => s.getAllKeys());
    return keys.map(String);
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Room on the device
// ---------------------------------------------------------------------------

/**
 * Is there room for `bytes`, with headroom?
 *
 * `navigator.storage.estimate()` is approximate and not everywhere. An absent
 * estimate reads as "probably fine" rather than "refuse": refusing every
 * capture on a browser that will not answer would break the surface for
 * everyone on it, and the real write below still fails honestly if it is full.
 */
export async function hasRoomFor(bytes: number): Promise<boolean> {
  try {
    const estimate = await navigator?.storage?.estimate?.();
    if (!estimate || estimate.quota == null || estimate.usage == null) return true;
    return estimate.quota - estimate.usage >= bytes * HEADROOM_FACTOR;
  } catch {
    return true;
  }
}

// ---------------------------------------------------------------------------
// Preparing a capture
// ---------------------------------------------------------------------------

/** Dimensions that fit inside a square of `edge`, keeping the aspect ratio.
 *  Never enlarges: a 600px photo is left at 600px. */
export function fitWithin(
  width: number, height: number, edge: number = MAX_EDGE,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= edge || longest === 0) {
    return { width: Math.round(width), height: Math.round(height) };
  }
  const scale = edge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

async function decode(file: Blob): Promise<{ source: CanvasImageSource; width: number; height: number }> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    return { source: bitmap, width: bitmap.width, height: bitmap.height };
  }
  // Older Safari. The object URL is revoked either way.
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('That file could not be read as an image'));
      el.src = url;
    });
    return { source: img, width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Turn a camera capture into something worth queueing, or refuse it.
 *
 * Rules 1 and 2 from the header, in order. Rule 3 belongs to `acceptPhoto`,
 * which is what a capture surface should actually call.
 */
export async function downscale(file: Blob): Promise<Blob> {
  const { source, width, height } = await decode(file);
  const target = fitWithin(width, height);

  const canvas = document.createElement('canvas');
  canvas.width = target.width;
  canvas.height = target.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser could not resize the photo');
  ctx.drawImage(source, 0, 0, target.width, target.height);
  if ('close' in source && typeof (source as ImageBitmap).close === 'function') {
    (source as ImageBitmap).close();
  }

  const out = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
  );
  if (!out) throw new Error('This browser could not resize the photo');
  // A re-encode that came out BIGGER — a small PNG screenshot, say — is worse
  // than the original. Keep whichever is smaller.
  return out.size < file.size ? out : file;
}

/**
 * The whole capture policy in one call: downscale, cap, check for room, store.
 *
 * Returns the id to put on the queue item. THROWS on refusal, and a caller
 * must show the message and NOT proceed — an accepted capture with no photo
 * behind it is the silent loss this exists to end.
 */
export async function acceptPhoto(file: Blob, id: string): Promise<{ id: string; bytes: number }> {
  const prepared = await downscale(file);
  if (prepared.size > MAX_PHOTO_BYTES) throw new PhotoTooLargeError(prepared.size);
  if (!(await hasRoomFor(prepared.size))) throw new DeviceFullError();
  await putPhoto(id, prepared);
  return { id, bytes: prepared.size };
}
